import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  COVER_PHOTO_API_URL,
  COVER_PHOTO_BY_ID_QUERY_KEY,
  GET_EVENT_COVER_PHOTOS
} from '@/constants';
import { apiUrl } from '@/helpers';
import { CoverPhotoPayload, ErrorResponse, Options } from '@/types';

export const coverPhotoEndpoints = {
  createCoverPhoto: (payload: CoverPhotoPayload) =>
    axios.post(`${apiUrl()}${COVER_PHOTO_API_URL}`, payload),
  getCoverPhoto: (eventId: number, id: number) =>
    axios.get(`${apiUrl()}${COVER_PHOTO_API_URL}/${eventId}/${id}`),
  updateCoverPhoto: (payload: CoverPhotoPayload) =>
    axios.put(`${apiUrl()}${COVER_PHOTO_API_URL}`, payload),
  getEventCoverPhotos: (eventId: number) =>
    axios.get(`${apiUrl()}${COVER_PHOTO_API_URL}/coverPhoto/${eventId}`),
  deleteCoverPhoto: (id: number) => axios.delete(`${apiUrl()}${COVER_PHOTO_API_URL}/${id}`),
  generateCustomAttributeValues: (eventId: number) =>
    axios.get(`${apiUrl()}${COVER_PHOTO_API_URL}/${eventId}`)
};

export const useCreateCoverPhotoMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(coverPhotoEndpoints.createCoverPhoto, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([GET_EVENT_COVER_PHOTOS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetCoverPhotoQuery = (
  eventId: number | null,
  id: number | null,
  options?: Options,
  enabled?: boolean
) => {
  if (!eventId || !id) {
    return { data: null, isLoading: false, isError: false };
  }
  return useQuery(
    [COVER_PHOTO_BY_ID_QUERY_KEY, { eventId, id }],
    () => coverPhotoEndpoints.getCoverPhoto(eventId, id),
    {
      retry: false,
      enabled: !!id && !!eventId && (enabled ?? true),
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useUpdateCoverPhoto = (queryClient: QueryClient, options: Options) => {
  return useMutation(coverPhotoEndpoints.updateCoverPhoto, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_EVENT_COVER_PHOTOS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetEventCoverPhotos = (eventId: number) => {
  return useQuery(
    [GET_EVENT_COVER_PHOTOS, { eventId }],
    () => coverPhotoEndpoints.getEventCoverPhotos(eventId),
    {
      enabled: !!eventId
    }
  );
};

export const useDeleteCoverPhotoMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(coverPhotoEndpoints.deleteCoverPhoto, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_EVENT_COVER_PHOTOS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGenerateCustomAttributesQuery = (eventId: number, enabled?: boolean) => {
  return useQuery(
    [GET_EVENT_COVER_PHOTOS, { eventId }],
    () => coverPhotoEndpoints.generateCustomAttributeValues(eventId),
    {
      enabled: !!eventId && (enabled ?? true)
    }
  );
};
